// Inference Chat Binder - Shared utility to wire AI inference events to ChatArea
// Tools fully own their row HTML; non-tool rows are rendered by core bubble controllers.

import { logger } from "../../../core/logger.js";
import {
  assistantGeneratingBubbleController,
  assistantTextBubbleController,
  assistantThinkingBubbleController,
  assistantToolBubbleController,
} from "../controllers/index.js";

function createToolBubbleConfig(props = {}) {
  return assistantToolBubbleController.createToolBubble(props);
}

function getSegmentController(segmentType) {
  if (segmentType === "text") {
    return assistantTextBubbleController;
  }
  if (segmentType === "reasoning") {
    return assistantThinkingBubbleController;
  }
  return null;
}

/**
 * Bind AI inference system events to ChatArea for full UI integration.
 */
export function bindInferenceToChatArea({
  aiInferenceSystem,
  aiToolSystem,
  chatArea,
  options = {},
}) {
  const {
    onInferenceFinished = () => {},
    onError = () => {},
    onComplete = () => {},
    persistToolCallToSegment = false,
    logTagPrefix = "inference|chat",
    getBubbleStyle: getBubbleStyleOverride,
  } = options;

  const segmentBubbleMap = new Map();
  const toolCallBubbleMap = new Map();
  const toolUiHtmlByToolCallId = new Map();
  const queuedSegmentUpdates = new Map();
  const queuedToolParamChunks = new Map();
  let generatingBubbleId = null;
  let segmentFlushFrameId = null;
  let toolParamFlushFrameId = null;
  const cancelledToolCallIds = new Set();

  function scheduleFrame(callback) {
    if (typeof requestAnimationFrame === "function") {
      return requestAnimationFrame(callback);
    }
    return setTimeout(callback, 16);
  }

  function cancelScheduledFrame(frameId) {
    if (!frameId) return;
    if (typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(frameId);
      return;
    }
    clearTimeout(frameId);
  }

  function getToolBubbleStyle(functionName) {
    if (typeof getBubbleStyleOverride === "function") {
      return getBubbleStyleOverride(functionName);
    }
    return aiToolSystem?.getToolInstance?.(functionName, null)?.bubbleStyle || "simple";
  }

  const listeners = [];

  function addListener(eventName, handler) {
    aiInferenceSystem.addEventListener(eventName, handler);
    listeners.push({ eventName, handler });
  }

  function updateToolBubbleUiHtml(toolCallId, html) {
    toolUiHtmlByToolCallId.set(toolCallId, html);
    const bubbleId = toolCallBubbleMap.get(toolCallId);
    if (!bubbleId) {
      return false;
    }
    if (typeof chatArea?.updateToolUiHtml === "function") {
      chatArea.updateToolUiHtml(bubbleId, html);
      return true;
    }
    if (typeof chatArea?.updateBubbleHtml === "function") {
      chatArea.updateBubbleHtml(bubbleId, html);
      return true;
    }
    return false;
  }

  function clearToolBubbleUiHtml(toolCallId) {
    toolUiHtmlByToolCallId.delete(toolCallId);
    return updateToolBubbleUiHtml(toolCallId, "");
  }

  function resolveToolBubbleUiApi(toolCallId) {
    const bubbleId = toolCallBubbleMap.get(toolCallId);
    if (!bubbleId || typeof chatArea?.getBubbleUiApi !== "function") {
      return { bubbleId, bubbleUiApi: null };
    }

    return {
      bubbleId,
      bubbleUiApi: chatArea.getBubbleUiApi(bubbleId),
    };
  }

  function buildToolUiApi(toolCallId) {
    const getBubbleUiApi = () => resolveToolBubbleUiApi(toolCallId).bubbleUiApi;

    return {
      updateToolUiHtml: (html) => updateToolBubbleUiHtml(toolCallId, html),
      clearToolUi: () => clearToolBubbleUiHtml(toolCallId),
      updateBubbleHtml: (html, rawContent) =>
        getBubbleUiApi()?.updateBubbleHtml?.(html, rawContent),
      clearBubbleHtml: () => getBubbleUiApi()?.clearBubbleHtml?.(),
      getRootElement: () => getBubbleUiApi()?.getRootElement?.() || null,
      query: (selector) => getBubbleUiApi()?.query?.(selector) || null,
      queryAll: (selector) => getBubbleUiApi()?.queryAll?.(selector) || [],
      patchBubbleDom: (patcher) => getBubbleUiApi()?.patchBubbleDom?.(patcher) || false,
      updateBubbleState: (updates) => getBubbleUiApi()?.updateBubbleState?.(updates),
      updateBubbleDurationText: (text) =>
        getBubbleUiApi()?.updateBubbleDurationText?.(text),
      updateBubble: (bubbleId, updates) =>
        getBubbleUiApi()?.updateBubble?.(bubbleId, updates),
      handleOpenFileByPath: (path) =>
        getBubbleUiApi()?.handleOpenFileByPath?.(path),
      handleOpenUrl: (url) => getBubbleUiApi()?.handleOpenUrl?.(url),
      handleBubbleClickAction: (action) =>
        getBubbleUiApi()?.handleBubbleClickAction?.(action),
    };
  }

  function updateToolBubbleState(toolCallId, updates = {}) {
    const bubbleId = toolCallBubbleMap.get(toolCallId);
    if (!bubbleId || !updates || typeof updates !== "object") {
      return false;
    }

    if (typeof chatArea?.updateBubbleState === "function") {
      chatArea.updateBubbleState(bubbleId, updates);
      return true;
    }

    if (typeof chatArea?.updateBubble === "function") {
      chatArea.updateBubble(bubbleId, updates);
      return true;
    }

    return false;
  }

  function applyQueuedSegmentUpdate(segmentId) {
    const queued = queuedSegmentUpdates.get(segmentId);
    if (!queued) {
      return;
    }

    queuedSegmentUpdates.delete(segmentId);

    const bubbleId = segmentBubbleMap.get(segmentId) || queued.bubbleId;
    if (!bubbleId) {
      return;
    }

    const segment = queued.segment;
    const updates = {
      content: segment.content || "",
      timestamp:
        segment.endTimestamp || segment.startTimestamp || new Date(),
      ...(segment.type === "reasoning"
        ? {
            startTimestamp: segment.startTimestamp || new Date(),
            endTimestamp: new Date(),
            isDurationActive: true,
          }
        : {}),
    };

    if (typeof chatArea?.updateBubbleState === "function") {
      chatArea.updateBubbleState(bubbleId, updates);
      return;
    }

    if (typeof chatArea?.updateBubble === "function") {
      chatArea.updateBubble(bubbleId, updates);
    }
  }

  function flushQueuedSegmentUpdates() {
    segmentFlushFrameId = null;
    const queuedIds = [...queuedSegmentUpdates.keys()];
    queuedIds.forEach((segmentId) => applyQueuedSegmentUpdate(segmentId));
  }

  function queueSegmentUpdate(segmentId, bubbleId, segment) {
    if (!segmentId || !bubbleId || !segment) {
      return;
    }

    queuedSegmentUpdates.set(segmentId, {
      bubbleId,
      segment,
    });

    if (segmentFlushFrameId == null) {
      segmentFlushFrameId = scheduleFrame(flushQueuedSegmentUpdates);
    }
  }

  function applyQueuedToolParamChunk(toolCallId) {
    const queued = queuedToolParamChunks.get(toolCallId);
    if (!queued) {
      return;
    }

    queuedToolParamChunks.delete(toolCallId);

    const { functionName, paramsChunk, paramsTextSoFar, index } = queued;
    if (!toolCallId || !toolCallBubbleMap.get(toolCallId)) {
      return;
    }

    const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
    void invokeToolBubbleHook(
      toolInstance,
      "onToolBubbleParamsChunk",
      {
        toolCallId,
        functionName,
        callIndex: index,
        paramsChunk,
        paramsTextSoFar,
      },
      toolCallId
    );
  }

  function flushQueuedToolParamChunks() {
    toolParamFlushFrameId = null;
    const queuedIds = [...queuedToolParamChunks.keys()];
    queuedIds.forEach((toolCallId) => applyQueuedToolParamChunk(toolCallId));
  }

  function queueToolParamChunk(toolCallChunk) {
    const { toolCallId } = toolCallChunk || {};
    if (!toolCallId) {
      return;
    }

    queuedToolParamChunks.set(toolCallId, toolCallChunk);

    if (toolParamFlushFrameId == null) {
      toolParamFlushFrameId = scheduleFrame(flushQueuedToolParamChunks);
    }
  }

  function createToolUiActionHandler(functionName, toolCallId) {
    return (actionId, payload, context = {}) => {
      const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
      if (!toolInstance || typeof toolInstance.handleToolUiAction !== "function") {
        return false;
      }
      return toolInstance.handleToolUiAction(actionId, payload, {
        ...context,
        toolCallId,
        functionName,
        aiToolSystem,
        aiInferenceSystem,
      });
    };
  }

  function persistToolBubbleSegment(messageId, toolCallId, updates = {}) {
    const message = aiInferenceSystem?.chat?.getMessage?.(messageId);
    if (!message?.data?.segments || typeof message.updateSegment !== "function") {
      return;
    }

    for (let index = 0; index < message.data.segments.length; index += 1) {
      const segment = message.data.segments[index];
      const segmentToolCallId =
        segment.toolCallId ||
        segment.content?.tool_call_id ||
        segment.content?.toolCallId ||
        segment.content?.id;
      if (segmentToolCallId !== toolCallId) {
        continue;
      }
      message.updateSegment(index, updates);
      break;
    }
  }

  function invokeToolBubbleHook(toolInstance, hookName, state, toolCallId) {
    return assistantToolBubbleController.invokeToolHook(
      toolInstance,
      hookName,
      state,
      toolCallId,
      buildToolUiApi(toolCallId)
    );
  }

  addListener("onBeginSegment", (messageId, segment) => {
    logger.log(
      { tags: `${logTagPrefix}|segment|begin`, color1: "cyan" },
      `Beginning segment: ${segment.type}`,
      { messageId, segmentId: segment.id, type: segment.type }
    );

    if (segment.type === "tool_call") {
      return;
    }

    const controller = getSegmentController(segment.type);
    if (!controller || !chatArea?.createBubble) {
      return;
    }

    const bubbleId = chatArea.createBubble(
      controller.createBubble({
        content: "",
        segmentId: segment.id,
        messageId,
        timestamp: segment.startTimestamp || new Date(),
        showTimestamp: true,
        ...(segment.type === "reasoning"
          ? {
              startTimestamp: segment.startTimestamp || new Date(),
              endTimestamp: null,
              isDurationActive: true,
              isExpanded: true,
              durationDisplayMode: "formatted",
            }
          : {}),
      })
    );
    if (bubbleId) {
      segmentBubbleMap.set(segment.id, bubbleId);
    }
  });

  addListener("onUpdateSegment", (messageId, segmentId, segment) => {
    const bubbleId = segmentBubbleMap.get(segmentId);
    if (!bubbleId) {
      return;
    }

    queueSegmentUpdate(segmentId, bubbleId, segment);
  });

  addListener("onEndSegment", (messageId, segmentId, segment) => {
    logger.log(
      { tags: `${logTagPrefix}|segment|end`, color1: "green" },
      `Ending segment: ${segment.type}`,
      { messageId, segmentId, totalContentLength: segment.content?.length }
    );

    if (segment.type === "tool_call") {
      return;
    }

    const bubbleId = segmentBubbleMap.get(segmentId);
    if (!bubbleId) {
      return;
    }

    if (
      segment.type === "reasoning"
      && !(segment.content || "").trim()
    ) {
      if (typeof chatArea?.removeBubble === "function") {
        chatArea.removeBubble(bubbleId);
      }
      segmentBubbleMap.delete(segmentId);
      return;
    }

    if (!chatArea?.updateBubble) {
      return;
    }

    applyQueuedSegmentUpdate(segmentId);

    chatArea.updateBubble(bubbleId, {
      content: segment.content || "",
      timestamp: segment.endTimestamp || segment.startTimestamp || new Date(),
      isComplete: true,
      ...(segment.type === "reasoning"
        ? {
            startTimestamp: segment.startTimestamp || new Date(),
            endTimestamp: segment.endTimestamp || new Date(),
            isDurationActive: false,
          }
        : {}),
    });
    segmentBubbleMap.delete(segmentId);
  });

  addListener("onInferencePrepared", (messageId) => {
    if (!chatArea?.createBubble) {
      return;
    }
    generatingBubbleId = chatArea.createBubble(
      assistantGeneratingBubbleController.createBubble({
        messageId,
        showTimestamp: false,
      })
    );
  });

  addListener("onStreamStart", () => {});
  addListener("onStreamIdle", () => {});

  addListener("onInitialConnectionPhaseEnd", () => {
    if (generatingBubbleId && chatArea?.removeBubble) {
      chatArea.removeBubble(generatingBubbleId);
      generatingBubbleId = null;
    }
  });

  addListener("onToolCallFunctionKnown", (messageId, toolCallInfo) => {
    if (cancelledToolCallIds.has(toolCallInfo?.toolCallId)) {
      return;
    }
    if (toolCallBubbleMap.has(toolCallInfo.toolCallId)) {
      return;
    }
    if (chatArea?.getBubbles) {
      const existing = chatArea
        .getBubbles()
        .find((b) => b.toolCallId === toolCallInfo.toolCallId);
      if (existing) {
        toolCallBubbleMap.set(toolCallInfo.toolCallId, existing.bubbleId);
        return;
      }
    }

    const toolInstance = aiToolSystem?.getToolInstance?.(
      toolCallInfo.functionName,
      null
    );

    if (chatArea?.createBubble) {
      const bubbleId = chatArea.createBubble(
        createToolBubbleConfig({
          content: {
            tool_calls: [
              {
                function: {
                  name: toolCallInfo.functionName,
                  arguments: {},
                },
              },
            ],
          },
          functionName: toolCallInfo.functionName,
          messageId,
          responseMessageId: messageId,
          toolCallId: toolCallInfo.toolCallId,
          status: "accumulating_arguments",
          showTimestamp: false,
          bubbleStyle: getToolBubbleStyle(toolCallInfo.functionName),
          toolUiHtml: "",
          onToolUiAction: createToolUiActionHandler(
            toolCallInfo.functionName,
            toolCallInfo.toolCallId
          ),
        })
      );
      if (bubbleId) {
        toolCallBubbleMap.set(toolCallInfo.toolCallId, bubbleId);
      }
    }

    void invokeToolBubbleHook(
      toolInstance,
      "onToolBubbleInit",
      {
        toolCallId: toolCallInfo.toolCallId,
        functionName: toolCallInfo.functionName,
        callIndex: toolCallInfo.index,
        paramsTextSoFar: "",
      },
      toolCallInfo.toolCallId
    );
  });

  addListener("onToolCallParamsChunk", (messageId, toolCallChunk) => {
    const { toolCallId, functionName, paramsChunk, paramsTextSoFar, index } =
      toolCallChunk || {};
    if (!toolCallId || !toolCallBubbleMap.get(toolCallId)) {
      return;
    }

    queueToolParamChunk({
      toolCallId,
      functionName,
      paramsChunk,
      paramsTextSoFar,
      index,
    });
  });

  addListener("onToolCallCleanup", (messageId, data) => {
    const pendingIds = data?.pendingToolCallIds || [];
    const executingIds = data?.executingToolCallIds || [];

    // Mark both pending and executing tool calls as cancelled so we don't
    // recreate UI from late local stream events.
    for (const toolCallId of pendingIds) {
      cancelledToolCallIds.add(toolCallId);
      queuedToolParamChunks.delete(toolCallId);
    }
    for (const toolCallId of executingIds) {
      cancelledToolCallIds.add(toolCallId);
      queuedToolParamChunks.delete(toolCallId);
    }

    // Pending tool-call bubbles get removed immediately (they were not persisted yet).
    if (pendingIds.length > 0 && chatArea?.removeBubble) {
      for (const toolCallId of pendingIds) {
        let bubbleId = toolCallBubbleMap.get(toolCallId);
        if (!bubbleId && chatArea?.getBubbles) {
          bubbleId = chatArea
            .getBubbles()
            .find((b) => b.toolCallId === toolCallId)?.bubbleId;
        }
        if (bubbleId) {
          toolCallBubbleMap.delete(toolCallId);
          toolUiHtmlByToolCallId.delete(toolCallId);
          chatArea.removeBubble(bubbleId);
        }
      }
    }

    // Executing tool-call bubbles are kept, but we flip them into a cancelled state
    // so tool UI stops spinners and doesn't get overwritten by onToolCallCompleted.
    if (executingIds.length > 0 && chatArea?.updateBubble) {
      for (const toolCallId of executingIds) {
        let bubbleId = toolCallBubbleMap.get(toolCallId);
        let bubble = null;
        if (!bubbleId && chatArea?.getBubbles) {
          bubble = chatArea.getBubbles().find((b) => b.toolCallId === toolCallId) || null;
          bubbleId = bubble?.bubbleId || null;
        } else if (bubbleId && chatArea?.getBubbles) {
          bubble = chatArea.getBubbles().find((b) => b.bubbleId === bubbleId) || null;
        }

        if (!bubbleId) continue;

        // Keep execution state in the bubble record without forcing a tool HTML
        // re-render from the wrapper/controller layer.
        updateToolBubbleState(toolCallId, {
          status: "cancelled",
          executionEndTime: Date.now(),
        });

        // Note: cancelling while a tool call is already executing does not
        // change the tool's internal execution (we can't reliably abort).
        // We therefore do not invoke any "cancelled bubble UI" hook here.
      }
    }
  });

  addListener("onToolCallStarted", (messageId, toolCallData) => {
    if (cancelledToolCallIds.has(toolCallData?.id)) {
      return;
    }
    applyQueuedToolParamChunk(toolCallData?.id);
    let bubbleId = toolCallBubbleMap.get(toolCallData.id);
    let createdBubbleAtStart = false;
    if (!bubbleId && chatArea?.getBubbles) {
      bubbleId = chatArea.getBubbles().find((b) => b.toolCallId === toolCallData.id)
        ?.bubbleId;
    }
    if (!bubbleId && chatArea?.createBubble) {
      const toolName = toolCallData.function?.name || "Unknown Function";
      const args =
        typeof toolCallData.function?.arguments === "string"
          ? (() => {
              try {
                return JSON.parse(toolCallData.function.arguments);
              } catch {
                return {};
              }
            })()
          : toolCallData.function?.arguments || {};
      bubbleId = chatArea.createBubble(
        createToolBubbleConfig({
          content: {
            tool_calls: [
              {
                function: {
                  name: toolName,
                  arguments: args,
                },
              },
            ],
          },
          functionName: toolName,
          messageId,
          responseMessageId: messageId,
          toolCallId: toolCallData.id,
          status: "executing",
          showTimestamp: false,
          bubbleStyle: getToolBubbleStyle(toolName),
          toolUiHtml: toolUiHtmlByToolCallId.get(toolCallData.id) || "",
          onToolUiAction: createToolUiActionHandler(toolName, toolCallData.id),
        })
      );
      if (bubbleId) {
        toolCallBubbleMap.set(toolCallData.id, bubbleId);
        createdBubbleAtStart = true;
      }
    }
    if (bubbleId && createdBubbleAtStart) {
      const toolInstance = aiToolSystem?.getToolInstance?.(
        toolCallData.function?.name,
        null
      );
      void invokeToolBubbleHook(
        toolInstance,
        "onToolBubbleInit",
        {
          toolCallId: toolCallData.id,
          functionName: toolCallData.function?.name,
          callIndex: toolCallData.index,
          paramsTextSoFar:
            typeof toolCallData.function?.arguments === "string"
              ? toolCallData.function.arguments
              : JSON.stringify(toolCallData.function?.arguments || {}),
        },
        toolCallData.id
      );
    }
    const executionStartTime = Date.now();

    const toolInstance = aiToolSystem?.getToolInstance?.(
      toolCallData.function?.name,
      null
    );
    let args = toolCallData.function?.arguments;
    if (typeof args === "string") {
      try {
        args = JSON.parse(args);
      } catch {
        args = {};
      }
    }

    void invokeToolBubbleHook(
      toolInstance,
      "onToolBubbleStarted",
      {
        toolCallId: toolCallData.id,
        functionName: toolCallData.function?.name,
        callIndex: toolCallData.index,
        args: args || {},
        status: "executing",
        executionStartTime,
        bubbleStyle: getToolBubbleStyle(toolCallData.function?.name),
      },
      toolCallData.id
    );
  });

  addListener("onToolCallCompleted", (messageId, toolCallData, result) => {
    applyQueuedToolParamChunk(toolCallData?.id);
    let bubbleId = toolCallBubbleMap.get(toolCallData.id);
    if (!bubbleId && chatArea?.getBubbles) {
      bubbleId = chatArea.getBubbles().find((b) => b.toolCallId === toolCallData.id)
        ?.bubbleId;
    }
    if (!bubbleId) {
      return;
    }

    if (cancelledToolCallIds.has(toolCallData.id)) {
      // If we were cancelled mid-tool, don't let completed overwrite the cancelled UI.
      updateToolBubbleState(toolCallData.id, {
        status: "cancelled",
      });
      if (persistToolCallToSegment) {
        persistToolBubbleSegment(messageId, toolCallData.id, {
          toolStatus: "cancelled",
          toolUiHtml: toolUiHtmlByToolCallId.get(toolCallData.id) || "",
          bubbleStyle: getToolBubbleStyle(toolCallData.function?.name),
        });
      }
      return;
    }

    let args = toolCallData.function.arguments;
    if (typeof args === "string") {
      try {
        args = JSON.parse(args);
      } catch {
        args = {};
      }
    }
    args = args || {};
    const toolInstance = aiToolSystem?.getToolInstance?.(
      toolCallData.function.name,
      null
    );
    const executionEndTime = Date.now();
    const completionStatus =
      result?.success === false ? "failed" : "completed";

    void invokeToolBubbleHook(
      toolInstance,
      "onToolBubbleCompleted",
      {
        toolCallId: toolCallData.id,
        functionName: toolCallData.function?.name,
        callIndex: toolCallData.index,
        args,
        result,
        status: completionStatus,
        executionEndTime,
        bubbleStyle: getToolBubbleStyle(toolCallData.function.name),
      },
      toolCallData.id
    ).then(() => {
      const finalHtml = toolUiHtmlByToolCallId.get(toolCallData.id) || "";

      if (persistToolCallToSegment) {
        persistToolBubbleSegment(messageId, toolCallData.id, {
          toolUiHtml: finalHtml,
          toolStatus: result?.success === false ? "failed" : "completed",
          bubbleStyle: getToolBubbleStyle(toolCallData.function?.name),
        });
      }
    });
  });

  addListener("onToolCallsReady", (messageId, toolCalls) => {
    if (!chatArea?.updateBubble || !chatArea?.createBubble) {
      return;
    }

    const allBubbles = chatArea.getBubbles?.() || [];
    for (const toolCall of toolCalls) {
      let bubbleId = toolCallBubbleMap.get(toolCall.id);
      if (!bubbleId) {
        bubbleId = allBubbles.find((bubble) => bubble.toolCallId === toolCall.id)
          ?.bubbleId;
      }

      const args = toolCall.function.arguments || {};
      const bubbleUpdates = {
        content: {
          tool_calls: [{ function: { name: toolCall.function.name, arguments: args } }],
        },
        status: "ready_for_execution",
        bubbleStyle: getToolBubbleStyle(toolCall.function.name),
      };

      if (bubbleId) {
        updateToolBubbleState(toolCall.id, bubbleUpdates);
      } else {
        const newBubbleId = chatArea.createBubble(
          createToolBubbleConfig({
            ...bubbleUpdates,
            functionName: toolCall.function.name,
            messageId,
            responseMessageId: messageId,
            toolCallId: toolCall.id,
            showTimestamp: false,
            toolUiHtml: toolUiHtmlByToolCallId.get(toolCall.id) || "",
            onToolUiAction: createToolUiActionHandler(
              toolCall.function.name,
              toolCall.id
            ),
          })
        );
        if (newBubbleId) {
          toolCallBubbleMap.set(toolCall.id, newBubbleId);
        }
      }
    }
  });

  addListener("onComplete", (messageId, data) => {
    onComplete(messageId, data);
  });

  addListener("onError", (messageId, error) => {
    onError(messageId, error);
  });

  // Unified error envelope (see inferenceErrorEnvelope.js).
  // AIInferenceSystem._handleInferenceErrorChunk already funnels every error path
  // through `_handleError` -> `onError`, so these listeners are observers only
  // (no re-emit). The normalized envelope is also attached to the Error passed
  // to `onError` as `error.revolt` / `error.provider` / `error.diagnostics`.
  addListener("onInferenceError", (messageId, normalized) => {
    logger.log(
      { tags: `${logTagPrefix}|inference-error`, color1: "red" },
      `Inference error (${normalized?.revolt?.errorCode || "UNKNOWN"}) on ${messageId}`,
      normalized
    );
  });

  addListener("onAbnormalTermination", () => {
    // Intentionally observer-only: abnormal_termination now routes through
    // _handleInferenceErrorChunk -> _handleError -> onError. This listener
    // exists so the event surface is documented and discoverable.
  });

  addListener("onInferenceFinished", (messageId, data) => {
    if (generatingBubbleId && chatArea?.removeBubble) {
      chatArea.removeBubble(generatingBubbleId);
      generatingBubbleId = null;
    }
    onInferenceFinished(messageId, data);
  });

  return {
    cleanup() {
      for (const { eventName, handler } of listeners) {
        aiInferenceSystem.removeEventListener(eventName, handler);
      }
      listeners.length = 0;
      cancelScheduledFrame(segmentFlushFrameId);
      cancelScheduledFrame(toolParamFlushFrameId);
      segmentFlushFrameId = null;
      toolParamFlushFrameId = null;
      segmentBubbleMap.clear();
      toolCallBubbleMap.clear();
      toolUiHtmlByToolCallId.clear();
      queuedSegmentUpdates.clear();
      queuedToolParamChunks.clear();
      generatingBubbleId = null;
    },
  };
}
